import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { AdminAuthProvider } from './context/AdminAuthContext';
import { ManagerAuthProvider } from './context/ManagerAuthContext';
import { CartProvider } from './context/CartContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import ManagerProtectedRoute from './components/ManagerProtectedRoute';
import AdminLayout from './layouts/AdminLayout';
import ManagerLayout from './layouts/ManagerLayout';
import StaffLogin from './pages/StaffLogin';
import ManagerDashboard from './pages/manager/ManagerDashboard';
import ManagerInventory from './pages/manager/ManagerInventory';
import ManagerOrders from './pages/manager/ManagerOrders';
import ManagerActivity from './pages/manager/ManagerActivity';
import ManagerProducts from './pages/manager/ManagerProducts';
import StorePage from './pages/StorePage';
import ProductDetailPage from './pages/ProductDetailPage';
import CheckoutPage from './pages/CheckoutPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailsPage from './pages/OrderDetailsPage';
import TrackOrderPage from './pages/TrackOrderPage';
import WishlistPage from './pages/WishlistPage';
import AccountPage from './pages/AccountPage';
import CouponsPage from './pages/CouponsPage';
import ProductAdminPanel from './pages/ProductAdminPanel';
import AdminOrdersPage from './pages/AdminOrdersPage';
import WarehouseManagement from './pages/WarehouseManagement';
import WarehouseManagersPage from './pages/WarehouseManagersPage';
import StaffManagement from './pages/StaffManagement';
import AdminActivityPage from './pages/AdminActivityPage';
// REMOVED: SKU System and Inventory Units
// import SKUManagementPage from './pages/SKUManagementPage';
// import InventoryUnitsPage from './pages/InventoryUnitsPage';
import CartPage from './pages/CartPage';
import LoginPage from './pages/Login';
import AdminDashboardMain from './pages/AdminDashboardMain';
import ReturnPolicy from './pages/ReturnPolicy';
import RefundPolicy from './pages/RefundPolicy';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Disclaimer from './pages/Disclaimer';
import AboutUs from './pages/AboutUs';
import ContactUs from './pages/ContactUs';
import './index.css';

// ScrollToTop component
const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Conditional Layout Components
const ConditionalNavbar = () => {
  const { pathname } = useLocation();
  if (pathname === '/login' || pathname.startsWith('/admin') || pathname.startsWith('/manager') || pathname.startsWith('/staff')) return null;
  return <Navbar />;
};

const ConditionalFooter = () => {
  const { pathname } = useLocation();
  if (pathname === '/login' || pathname.startsWith('/admin') || pathname.startsWith('/manager') || pathname.startsWith('/staff')) return null;
  return <Footer />;
};

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AdminAuthProvider>
        <ManagerAuthProvider>
        <AuthProvider>
          <CartProvider>
            <div className="min-h-screen bg-white">
            <ScrollToTop />
            <ConditionalNavbar />
            <main className="transition-all duration-500">
              <Routes>
                {/* Customer Routes */}
                <Route path="/" element={<StorePage />} />
                <Route path="/products/:productId" element={<ProductDetailPage />} />
                <Route path="/categories/:categoryName/products" element={<StorePage />} />
                <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
                <Route path="/order-success" element={<ProtectedRoute><OrderSuccessPage /></ProtectedRoute>} />
                <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
                <Route path="/orders/:orderIdentifier" element={<ProtectedRoute><OrderDetailsPage /></ProtectedRoute>} />
                <Route path="/track" element={<TrackOrderPage />} />
                <Route path="/wishlist" element={<ProtectedRoute><WishlistPage /></ProtectedRoute>} />
                <Route path="/coupons" element={<ProtectedRoute><CouponsPage /></ProtectedRoute>} />
                <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
                <Route path="/cart" element={<CartPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/return-policy" element={<ReturnPolicy />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/disclaimer" element={<Disclaimer />} />
                <Route path="/about" element={<AboutUs />} />
                <Route path="/contact" element={<ContactUs />} />

                {/* Unified Staff Login (Admin + Manager) */}
                <Route path="/staff/login" element={<StaffLogin />} />
                <Route path="/admin/login" element={<StaffLogin />} />
                <Route path="/manager/login" element={<StaffLogin />} />
                <Route path="/admin/dashboard" element={
                  <AdminProtectedRoute>
                    <AdminLayout>
                      <AdminDashboardMain />
                    </AdminLayout>
                  </AdminProtectedRoute>
                } />
                <Route path="/admin/products" element={
                  <AdminProtectedRoute>
                    <AdminLayout>
                      <ProductAdminPanel />
                    </AdminLayout>
                  </AdminProtectedRoute>
                } />
                <Route path="/admin/orders" element={
                  <AdminProtectedRoute>
                    <AdminLayout>
                      <AdminOrdersPage />
                    </AdminLayout>
                  </AdminProtectedRoute>
                } />
                <Route path="/admin/customers" element={
                  <AdminProtectedRoute>
                    <AdminLayout>
                      <div className="text-center py-20">
                        <h2 className="text-2xl font-black text-slate-900 mb-2">Customer Management</h2>
                        <p className="text-slate-500">Coming soon...</p>
                      </div>
                    </AdminLayout>
                  </AdminProtectedRoute>
                } />
                <Route path="/admin/analytics" element={
                  <AdminProtectedRoute>
                    <AdminLayout>
                      <div className="text-center py-20">
                        <h2 className="text-2xl font-black text-slate-900 mb-2">Analytics Dashboard</h2>
                        <p className="text-slate-500">Coming soon...</p>
                      </div>
                    </AdminLayout>
                  </AdminProtectedRoute>
                } />
                <Route path="/admin/coupons" element={
                  <AdminProtectedRoute>
                    <AdminLayout>
                      <div className="text-center py-20">
                        <h2 className="text-2xl font-black text-slate-900 mb-2">Coupon Management</h2>
                        <p className="text-slate-500">Coming soon...</p>
                      </div>
                    </AdminLayout>
                  </AdminProtectedRoute>
                } />
                <Route path="/admin/shipping" element={
                  <AdminProtectedRoute>
                    <AdminLayout>
                      <WarehouseManagement />
                    </AdminLayout>
                  </AdminProtectedRoute>
                } />
                <Route path="/admin/warehouse-managers" element={
                  <AdminProtectedRoute>
                    <AdminLayout>
                      <WarehouseManagersPage />
                    </AdminLayout>
                  </AdminProtectedRoute>
                } />
                <Route path="/admin/staff" element={
                  <AdminProtectedRoute>
                    <AdminLayout>
                      <StaffManagement />
                    </AdminLayout>
                  </AdminProtectedRoute>
                } />
                <Route path="/admin/activity" element={
                  <AdminProtectedRoute>
                    <AdminLayout>
                      <AdminActivityPage />
                    </AdminLayout>
                  </AdminProtectedRoute>
                } />
                {/* REMOVED: SKU System, Inventory Units, and Reviews */}
                {/*
                <Route path="/admin/skus" element={
                  <AdminProtectedRoute>
                    <AdminLayout>
                      <SKUManagementPage />
                    </AdminLayout>
                  </AdminProtectedRoute>
                } />
                <Route path="/admin/inventory-units" element={
                  <AdminProtectedRoute>
                    <AdminLayout>
                      <InventoryUnitsPage />
                    </AdminLayout>
                  </AdminProtectedRoute>
                } />
                <Route path="/admin/reviews" element={
                  <AdminProtectedRoute>
                    <AdminLayout>
                      <div className="text-center py-20">
                        <h2 className="text-2xl font-black text-slate-900 mb-2">Reviews Management</h2>
                        <p className="text-slate-500">Coming soon...</p>
                      </div>
                    </AdminLayout>
                  </AdminProtectedRoute>
                } />
                */}
                <Route path="/admin/notifications" element={
                  <AdminProtectedRoute>
                    <AdminLayout>
                      <div className="text-center py-20">
                        <h2 className="text-2xl font-black text-slate-900 mb-2">Notifications Center</h2>
                        <p className="text-slate-500">Coming soon...</p>
                      </div>
                    </AdminLayout>
                  </AdminProtectedRoute>
                } />
                <Route path="/admin/settings" element={
                  <AdminProtectedRoute>
                    <AdminLayout>
                      <div className="text-center py-20">
                        <h2 className="text-2xl font-black text-slate-900 mb-2">System Settings</h2>
                        <p className="text-slate-500">Coming soon...</p>
                      </div>
                    </AdminLayout>
                  </AdminProtectedRoute>
                } />

                {/* ── Warehouse Manager Portal ── */}
                <Route path="/manager/dashboard" element={<ManagerProtectedRoute><ManagerLayout><ManagerDashboard /></ManagerLayout></ManagerProtectedRoute>} />
                <Route path="/manager/products"   element={<ManagerProtectedRoute><ManagerLayout><ManagerProducts /></ManagerLayout></ManagerProtectedRoute>} />
                <Route path="/manager/inventory"  element={<ManagerProtectedRoute><ManagerLayout><ManagerInventory /></ManagerLayout></ManagerProtectedRoute>} />
                <Route path="/manager/orders"     element={<ManagerProtectedRoute><ManagerLayout><ManagerOrders /></ManagerLayout></ManagerProtectedRoute>} />
                <Route path="/manager/activity"   element={<ManagerProtectedRoute><ManagerLayout><ManagerActivity /></ManagerLayout></ManagerProtectedRoute>} />

              </Routes>
            </main>
            <ConditionalFooter />
          </div>
        </CartProvider>
      </AuthProvider>
      </ManagerAuthProvider>
      </AdminAuthProvider>
    </Router>
  );
}

export default App;
